import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCheck } from '@fortawesome/free-solid-svg-icons';
import groupService from '../../api/groupService';
import MembershipCard from './MembershipCard';
import CreateMembershipModal from './CreateMembershipModal';
import JoinRequests from './JoinRequests';
import { AdminPageLayout, AdminPageFilters } from '../common';

const MembershipsList = ({ groupId, searchQuery, isAdmin }) => {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentMembership, setCurrentMembership] = useState(null);
  const [localSearchQuery, setLocalSearchQuery] = useState('');

  // Fetch memberships for this group
  useEffect(() => {
    const fetchMemberships = async () => {
      try {
        setLoading(true);
        const fetchedMemberships = await groupService.getGroupMemberships(groupId);
        console.log('Fetched memberships:', fetchedMemberships);
        setMemberships(fetchedMemberships);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching memberships:', error);
        setError('Failed to load memberships. Please try again later.');
        setLoading(false);
      }
    };

    if (groupId) {
      fetchMemberships();
    }
  }, [groupId]);

  // Filter memberships based on search query
  const effectiveSearchQuery = localSearchQuery || searchQuery || '';
  const filteredMemberships = effectiveSearchQuery && memberships
    ? memberships.filter(membership => 
        membership.name.toLowerCase().includes(effectiveSearchQuery.toLowerCase()) ||
        (membership.description && membership.description.toLowerCase().includes(effectiveSearchQuery.toLowerCase()))
      )
    : memberships;

  // Handle editing a membership
  const handleEditMembership = (membership) => {
    setCurrentMembership(membership);
    setShowEditModal(true);
  };

  // Handle deleting a membership
  const handleDeleteMembership = async (membershipId) => {
    try {
      await groupService.deleteMembership(groupId, membershipId);
      // Remove deleted membership from state
      setMemberships(memberships.filter(m => m.id !== membershipId));
    } catch (error) {
      console.error('Error deleting membership:', error);
    }
  };

  // Handle successful membership update
  const handleMembershipUpdated = (updatedMembership) => {
    setMemberships(memberships.map(membership => 
      membership.id === updatedMembership.id ? updatedMembership : membership
    ));
    setShowEditModal(false);
    setCurrentMembership(null);
  };

  // Render empty state content
  const renderEmptyState = () => {
    // Show search-specific message if user is searching
    if (effectiveSearchQuery && effectiveSearchQuery.trim()) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FontAwesomeIcon icon={faUserCheck} size="4x" />
          </div>
          <h2>No Results Found</h2>
          <p>Try searching with different keywords</p>
        </div>
      );
    }
    
    // Show normal empty state when not searching
    return (
      <div className="empty-state">
        <div className="empty-state-icon">
          <FontAwesomeIcon icon={faUserCheck} size="4x" />
        </div>
        <h2>No Memberships Available</h2>
        <p>Create a membership option to get started</p>
      </div>
    );
  };

  // Prepare filter components
  const renderFilters = () => {
    if (!isAdmin) return null;
    
    return (
      <AdminPageFilters.FilterGroup>
        <AdminPageFilters.SearchBox
          value={localSearchQuery}
          onChange={setLocalSearchQuery}
          placeholder="Search memberships..."
        />
      </AdminPageFilters.FilterGroup>
    );
  };

  return (
    <AdminPageLayout
      title="Memberships"
      icon={<FontAwesomeIcon icon={faUserCheck} />}
      subtitle="Manage membership options and join requests"
      showFilters={isAdmin}
      filters={renderFilters()}
      loading={loading}
      error={error}
      emptyState={renderEmptyState()}
    >
      {/* Join Requests Section (only visible to admins) */}
      {isAdmin && (
        <div style={{ marginBottom: '24px' }}>
          <JoinRequests 
            groupId={groupId} 
            isAdmin={isAdmin}
            onRequestsUpdate={() => {
              // You can add any refresh logic here if needed
              // For example, refetch group member count, etc.
            }}
          />
        </div>
      )}
      
      {/* Memberships Section */}
      {filteredMemberships && filteredMemberships.length > 0 && (
        <div className="memberships-list">
          {filteredMemberships.map(membership => (
            <MembershipCard 
              key={membership.id} 
              membership={membership} 
              groupId={groupId}
              isAdmin={isAdmin}
              onEditMembership={handleEditMembership}
              onDeleteMembership={handleDeleteMembership}
            />
          ))}
        </div>
      )}
      
      {/* Edit Membership Modal */}
      {showEditModal && currentMembership && (
        <CreateMembershipModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setCurrentMembership(null);
          }}
          groupId={groupId}
          onMembershipUpdated={handleMembershipUpdated}
          membership={currentMembership}
          isEditMode={true}
        />
      )}
    </AdminPageLayout>
  );
};

export default MembershipsList;